
// Admin-region filter constants (see design: dynamic-admin-region-filter)
export const LEVEL1_KEY = 'admin_area_lv1';
export const LEVEL3_KEY = 'admin_area_lv3';
export const DEFAULT_STATE_LABEL = 'State';
export const DEFAULT_CITY_LABEL = 'City';
export const LABEL_LENGTH_BOUND = 60;
export const ADMIN_REGION_TIMEOUT_MS = 30000;

/**
 * Truncate a resolved administrative-region name to the Label_Length_Bound.
 * Validates that `name` is a non-empty (post-trim) string, then returns
 * `String(name).slice(0, LABEL_LENGTH_BOUND)`. Returns an empty string for
 * absent/non-string/blank input so callers can reject the update.
 * @param {string} name
 * @returns {string} truncated label, or '' when the input is invalid
 */
export function truncateLabel(name) {
  if (typeof name !== 'string' || name.trim() === '') return '';
  return String(name).slice(0, LABEL_LENGTH_BOUND);
}

/**
 * Build a Level_Name_Map from an Admin_Region_Response payload.
 * For each Admin_Region_Level { level, name, ... }, map level -> name.
 * Entries missing a truthy `level` or a `name` are skipped; all valid entries
 * are retained. Non-array / absent / empty payloads yield {}. Later duplicate
 * levels win.
 * @param {Array} payload
 * @returns {Object} level -> name
 */
export function buildLevelNameMap(payload) {
  if (!payload || !Array.isArray(payload) || payload.length === 0) return {};
  const map = {};
  payload.forEach((entry) => {
    if (!entry || !entry.level || entry.name === undefined || entry.name === null) return;
    map[entry.level] = entry.name;
  });
  return map;
}

/**
 * Given the current filter definitions and a Level_Name_Map, return a NEW
 * filter array with state-filter and city-filter relabeled/hidden per the map.
 * Pure and deterministic (no `this`, no I/O). Preserves each affected
 * definition's id/param/type/sources byte-for-byte; only `name` and `hidden`
 * may change. Filters other than state-filter/city-filter are returned
 * unchanged.
 *
 * - lv1 -> non-empty string: state-filter { name: truncateLabel(value), hidden: false }
 * - lv1 absent/empty:        state-filter { name: DEFAULT_STATE_LABEL,   hidden: true  }
 * - lv3 -> non-empty string: city-filter  { name: truncateLabel(value),  hidden: false }
 * - lv3 absent/empty:        city-filter  { name: DEFAULT_CITY_LABEL,    hidden: true  }
 *
 * @param {Array} filters current filter definitions
 * @param {Object} levelNameMap level -> name mapping
 * @returns {Array} new filter definitions
 */
export function applyAdminRegionLabels(filters, levelNameMap) {
  if (!Array.isArray(filters)) return [];
  const map = levelNameMap || {};
  const lv1 = map[LEVEL1_KEY];
  const lv3 = map[LEVEL3_KEY];
  return filters.map((item) => {
    if (!item) return item;
    if (item.id === 'state-filter') {
      return typeof lv1 === 'string' && lv1.trim() !== ''
        ? { ...item, name: truncateLabel(lv1), hidden: false }
        : { ...item, name: DEFAULT_STATE_LABEL, hidden: true };
    }
    if (item.id === 'city-filter') {
      return typeof lv3 === 'string' && lv3.trim() !== ''
        ? { ...item, name: truncateLabel(lv3), hidden: false }
        : { ...item, name: DEFAULT_CITY_LABEL, hidden: true };
    }
    return item;
  });
}

/**
 * Return a NEW filter array with state-filter and city-filter reset to their
 * default labels and made visible. Used when the country is cleared/emptied
 * or an admin-region lookup fails. Pure and deterministic (no `this`, no I/O).
 * Preserves each affected definition's id/param/type/sources byte-for-byte;
 * only `name` and `hidden` change. Other defs are returned unchanged.
 *
 * @param {Array} filters current filter definitions
 * @returns {Array} new filter definitions
 */
export function resetAdminRegionLabels(filters) {
  if (!Array.isArray(filters)) return [];
  return filters.map((item) => {
    if (!item) return item;
    if (item.id === 'state-filter') {
      return { ...item, name: DEFAULT_STATE_LABEL, hidden: false };
    }
    if (item.id === 'city-filter') {
      return { ...item, name: DEFAULT_CITY_LABEL, hidden: false };
    }
    return item;
  });
}


/**
 * Build the Clone_Payload for POST /v2/save-custom-place from the editor's
 * current fields, the source place's center coordinates, and the assembled
 * geometry. Pure and deterministic (no `this`, no I/O), mirroring
 * `truncateLabel`/`buildLevelNameMap`.
 *
 * Aborts (never submits) when preconditions fail:
 * - geometry absent  -> { ok: false, error: { code: 'GEOMETRY_REQUIRED', ... } } (R6.6)
 * - companyId absent -> { ok: false, error: { code: 'COMPANY_ID_REQUIRED', ... } } (R6.8)
 *
 * On success returns { ok: true, payload: { properties, geometry, companyId } }:
 * - properties.name/address/description are string forms ('' when null/absent) (R6.1)
 * - properties.labels is the tags array ([] when none) (R6.2)
 * - properties.lat/lon are included as Number only when the source value is
 *   non-null/non-empty (R6.3, R6.4)
 * - never includes near_poi_id (create, not update) (R5.6)
 *
 * Category and Brand map to the upstream `cat2` / `poi_list_name` fields
 * (matching the canonical save shape in PlaceSelectionModal). They are included
 * only when a non-empty value is provided so the create path never sends blanks.
 *
 * @param {{companyId:*, name:*, address:*, description:*, tags:*, category:*, brand:*, lat:*, lon:*, geometry:*}} args
 * @returns {{ok:true, payload:Object} | {ok:false, error:{code:string, message:string}}}
 */
export function buildClonePayload({ companyId, name, address, description, tags, category, brand, lat, lon, geometry }) {
  if (!geometry) {
    return { ok: false, error: { code: 'GEOMETRY_REQUIRED', message: 'A polygon is required.' } }; // R6.6
  }
  if (companyId === undefined || companyId === null || companyId === '') {
    return { ok: false, error: { code: 'COMPANY_ID_REQUIRED', message: 'companyId is required.' } }; // R6.8
  }
  const properties = {
    name: name == null ? '' : String(name), // R6.1
    address: address == null ? '' : String(address), // R6.1
    description: description == null ? '' : String(description), // R6.1
    // Tags map to the upstream "labels" field. R6.2.
    labels: Array.isArray(tags) ? tags : [],
  };
  // Category -> cat2, Brand -> poi_list_name. Include only when present so a
  // blank value never overwrites/clears the upstream field.
  if (category !== undefined && category !== null && String(category).trim() !== '') {
    properties.cat2 = String(category);
  }
  if (brand !== undefined && brand !== null && String(brand).trim() !== '') {
    properties.poi_list_name = String(brand);
  }
  // R6.3 / R6.4: include lat/lon only when present.
  if (lat !== undefined && lat !== null && lat !== '') properties.lat = Number(lat);
  if (lon !== undefined && lon !== null && lon !== '') properties.lon = Number(lon);
  return {
    ok: true,
    payload: {
      properties,
      geometry, // R5.5, R6.5
      companyId, // top-level (R6.7)
      // NOTE: no near_poi_id (R5.6, R6 create-not-update)
    },
  };
}


const PlacesFilters = [
  { id: 'sub-category-filter', param: 'cat2', name: 'Category', type: 'typeahead', sources: ['Azira', 'Custom'] },
  { id: 'brand-filter', param: 'brand', name: 'Brand', type: 'typeahead', sources: ['Azira', 'Custom'] },
  { id: 'country-filter', param: 'country', name: 'Country', type: 'typeahead', sources: ['Azira', 'Custom'], required: true },
  { id: 'state-filter', param: 'state', name: 'State', type: 'typeahead', sources: ['Azira', 'Custom'] },
  { id: 'city-filter', param: 'city', name: 'City', type: 'typeahead', sources: ['Azira', 'Custom'] },
  { id: 'places-type-filter', param: 'placesType', name: 'Place Type', type: 'static', sources: ['Custom'] },
  { id: 'tag-filter', param: 'tag', name: 'Tag', type: 'typeahead', sources: ['Custom'] },
];


const suggest_urls = {
  cat1: 'places-suggest?location_type=cat1',
  cat2: 'places-suggest?location_type=cat2',
  brand: 'places-suggest?location_type=brand',
  country: 'places-suggest?location_type=country',
  state: 'places-suggest?location_type=state',
  city: 'places-suggest?location_type=city',
}

export default {
  data() {
    // `filters` MUST be declared here so Vue 2 makes it reactive. When it was
    // only assigned in `created()` (`this.filters = ...`), reassigning it later
    // (e.g. the atomic `this.filters = next` in applyAdminRegionsForCountry) did
    // not trigger reactivity, so `availableFilters` never recomputed and the
    // State/City labels only updated on the next unrelated reactive change.
    // Each instance gets its own shallow copy of the definitions so mounts never
    // share/mutate the module-level PlacesFilters array.
    return {
      filters: PlacesFilters.map((f) => ({ ...f })),
    };
  },
  created() {
    // Reset to a fresh per-instance copy of the default definitions on mount.
    this.filters = PlacesFilters.map((f) => ({ ...f }));
  },
  methods: {
    setSearchParams(term) {
      const numFilters = this.filters.length;
      if (!this._paramList) this._paramList = {};
      //if (term !== "") {
        this._paramList['search_term'] = term;
        // this._paramList[`ands[${numFilters}].ors[0].or`] = `name~=${term}`;
        // this._paramList[`ands[${numFilters}].ors[1].or`] = `address~=${term}`;
        // this._paramList[`ands[${numFilters}].ors[2].or`] = `id~=${term}`;
      //}
      this._setDefaultParams();
    },
    clearSearchParams() {
      if (!this._paramList) return;
      const numFilters = this.filters.length;
      delete this._paramList[`search_term`];
      //delete this._paramList[`ands[${numFilters}].ors[0].or`];
      //delete this._paramList[`ands[${numFilters}].ors[1].or`];
      //delete this._paramList[`ands[${numFilters}].ors[2].or`];
    },
    clearAllParams() {
      this._paramList = {};
      this._selectedFilters = {};
    },
    _setDefaultParams() {
      if (!this._paramList) this._paramList = {};
      // this._paramList[`ands[8].and`] = 'type.name!=basic';
      // this._paramList[`ands[9].and`] = 'type.name!=Skipped';
      // this._paramList['include_polygon_category_children'] = true;
      // this._paramList['isInActive'] = true;
    },
    _setFilterParams() {
      if (!this._paramList) this._paramList = {};
      console.log(this._selectedFilters);
      this.filters.forEach((filter, idx) => {
        if (this._selectedFilters[filter.id] !== undefined && this._selectedFilters[filter.id].length) {
          //this._paramList[filter.param] = this._selectedFilters[filter.id][0];
          this._paramList[filter.param] = this._selectedFilters[filter.id].join("|");
        }else {
          delete this._paramList[filter.param];
        }
      });
      console.log(this._paramList);
      this._setDefaultParams();
    },
    setOrderBy(field, asc) {
      if (field !== 'name' && field !== 'address') throw new Error('Invalid order by field');
      this._paramList['order_by[0]'] = `${field}:${asc ? 'asc' : 'desc'}`;
    },
    setFilter(filter, ids) {
      if (!this._selectedFilters) this._selectedFilters = {};
      this._selectedFilters[filter.id] = ids;
      this._setFilterParams();
    },
    updateFiltersWithAdminRegions(admin_regions) {
      console.log(Object.keys(admin_regions));
      if(Object.keys(admin_regions).includes('admin_area_lv1')){
        this.filters = this.filters.map(item =>
          item.id === 'state-filter' ? { ...item, name: admin_regions['admin_area_lv1'] } : item
        );
      } /* else {
        this.filters = this.filters.filter(item => item.id !== 'state-filter'); //remove State filter
      }*/
      if(Object.keys(admin_regions).includes('admin_area_lv3')) {
        this.filters = this.filters.map(item =>
          item.id === 'city-filter' ? { ...item, name: admin_regions['admin_area_lv3'] } : item
        );
      } else {
        this.filters = this.filters.filter(item => item.id !== 'city-filter'); // remove City filter
      }
      console.log(this.filters);
    },
    resetFiltersToDefault(){
      this.filters = PlacesFilters;
    },
    hasUserParams() {
      return this._selectedFilters !== undefined && Object.keys(this._selectedFilters).length > 0 && (Object.values(this._selectedFilters).some(ids => ids.length > 0));
    },
    hasSearchParams() {
      if (!this._paramList)  return false;
      const numFilters = this.filters.length;

      return numFilters > 0; //`ands[${numFilters}].ors[0].or` in this._paramList;
    },
    async getAdminRegions(country) {
      let params = {};
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        if (country!=="") {
          params['country'] = country;
        };
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          params,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/get-admin-regions`
        });
        let result = {}
        response.data.forEach((item) => {
          result[item.level] = item.name;
        });
        return result;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    /**
     * Fetch the administrative-region level -> name mapping for a country.
     * GET {VUE_APP_PINNACLE_API}/v2/get-admin-regions?country={country}
     * (pinnacle-api proxy; the country is passed as a URL-encoded query
     * parameter via axios `params`).
     *
     * Follows the repo auth pattern (tokenNeedsRefresh -> refreshToken -> getHeaders).
     * The pinnacle-api proxy has already unwrapped the Places API
     * NearServiceResponseDto, so `response.data` is the bare payload: an array
     * of admin-region levels on success, or an empty object `{}` when empty.
     * There is NO `{ success, payload }` envelope at this layer, so the array is
     * passed to the pure `buildLevelNameMap` helper directly. On network error,
     * non-2xx status, or 30s timeout, logs via console.error and resolves to
     * `{ ok: false, levelNameMap: {} }` (never throws) so the caller can
     * distinguish "successful but level absent" (hide) from "failed" (keep
     * defaults). A successful 2xx response whose body is not an array (e.g. the
     * proxy's `{}` fallback) resolves to `{ ok: true, levelNameMap: {} }`.
     *
     * @param {string} country the Country_Value (option id)
     * @returns {Promise<{ ok: boolean, levelNameMap: Object }>}
     */
    async getAdminRegionsForCountry(country) {
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          params: { country },
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/get-admin-regions`,
          timeout: ADMIN_REGION_TIMEOUT_MS,
        });
        // The proxy returns the raw payload array directly (envelope stripped).
        // Any 2xx response is a success; buildLevelNameMap handles non-array
        // bodies by yielding an empty map.
        return { ok: true, levelNameMap: buildLevelNameMap(response.data) };
      } catch (err) {
        console.error('Failed to load admin regions for country', country, err);
        return { ok: false, levelNameMap: {} };
      }
    },
    async getTenantCountry(companyId) {
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const params = {};
        if (companyId) params.companyId = companyId;
        const response = await this.$http({
          method: 'GET',
          params,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/tenant-country`
        });
        // Backend returns { success, country: { id, name } | null }
        return response.data && response.data.country ? response.data.country : null;
      } catch (axiosError) {
        console.error('Failed to resolve tenant country', axiosError);
        return null;
      }
    },
    // Normalize a Places API v3 reference list (categories / poilists) into
    // typeahead options [{ id, name }], optionally filtered by a search term.
    _normalizeCustomOptions(payload, { idKey, searchText = '', limit } = {}) {
      const list = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.records) ? payload.records : []);
      const term = (searchText || '').trim().toLowerCase();
      const options = list
        .map((item) => {
          const name = item && (item.name || item.nameEn || item.nameNl || item.text);
          if (!name) return null;
          const id = item[idKey] !== undefined && item[idKey] !== null ? item[idKey] : name;
          return { id, name };
        })
        .filter((option) => option && (!term || option.name.toLowerCase().includes(term)))
        .filter((option, index, arr) => arr.findIndex((o) => o.name === option.name) === index);
      return limit ? options.slice(0, limit) : options;
    },
    async getCustomCategories(country, { companyId, searchText = '', limit } = {}) {
      if (!country) return [];
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const params = {};
        if (companyId) params.companyId = companyId;
        if (limit) params.limit = limit;
        const trimmedSearch = (searchText || '').trim();
        if (trimmedSearch) params.category = trimmedSearch;
        const response = await this.$http({
          method: 'GET',
          params,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/custom-categories/${encodeURIComponent(country)}`
        });
        const payload = response.data && response.data.payload !== undefined ? response.data.payload : response.data;
        return this._normalizeCustomOptions(payload, { idKey: 'nearCatId', searchText, limit });
      } catch (axiosError) {
        throw axiosError;
      }
    },
    async getCustomPoilists(country, { companyId, searchText = '', limit } = {}) {
      if (!country) return [];
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const params = {};
        if (companyId) params.companyId = companyId;
        if (limit) params.limit = limit;
        const trimmedSearch = (searchText || '').trim();
        if (trimmedSearch) params.poi_list = trimmedSearch;
        const response = await this.$http({
          method: 'GET',
          params,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/custom-poilists/${encodeURIComponent(country)}`
        });
        const payload = response.data && response.data.payload !== undefined ? response.data.payload : response.data;
        return this._normalizeCustomOptions(payload, { idKey: 'nearPoiListId', searchText, limit });
      } catch (axiosError) {
        throw axiosError;
      }
    },
    async getPlacesFilteredPolygons({page, page_size, filters = {}, overrideParams}) {
      let params;
      if (overrideParams !== undefined) params = overrideParams;
      else {
        params = {...this._paramList};
        if (page) params.page = page;
        if (page_size) params.page_size = page_size;
      }

      // Append filter values as query params (Azira source only supports these)
      if (filters.country) params.country = filters.country;
      if (filters.state) params.state = filters.state;
      if (filters.city) params.city = filters.city;
      if (filters.cat2) params.cat2 = filters.cat2;
      if (filters.brand) params.brand = filters.brand;
      // Place Type and Tag are NOT sent for Azira source

      console.log(params);
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        if (params['search_term']==undefined) {
          params['search_term'] = "";
        };
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          params,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-search`
        });
        return response.data;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    async getPlacesSearchPois({ page, page_size, companyId, custom = false, filters = {}, search_after, overrideBody }) {
      let body;
      if (overrideBody !== undefined) body = { ...overrideBody };
      else {
        body = { ...(this._paramList || {}) };
        if (page) body.page = page;
        if (page_size) body.page_size = page_size;
      }

      // Custom cursor path: forward the search_after cursor only when present.
      // page/page_size above are kept for backward compatibility (backend now
      // ignores them for the Custom source); the cursor drives Prev/Next.
      if (search_after) body.search_after = search_after;

      if (body.search_term === undefined) {
        body.search_term = "";
      }
      body.companyId = companyId;
      body.custom = custom;

      // Map filter values into POST body with defaults
      body.categories = filters.cat2 ? [filters.cat2] : [];
      body.brands = filters.brand ? [filters.brand] : [];
      body.country = filters.country || "";
      body.state = filters.state ? [filters.state] : [];
      body.city = filters.city ? [filters.city] : [];
      body.placesType = filters.placesType ? [filters.placesType] : [];
      body.labels = filters.tag ? [filters.tag] : [];

      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'POST',
          data: body,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-search-pois`
        });

        // Cursor contract: the backend returns { records, pagination: {
        // page_size, records, search_after, has_more } } at the TOP level
        // (payload is deleted server-side). Returning responseData therefore
        // surfaces both `records` and the `pagination` cursor object to the
        // caller. The payload-unwrap guard below remains only for the legacy
        // envelope shape.
        const responseData = response.data || {};
        if (responseData.payload && typeof responseData.payload === 'object') {
          return responseData.payload;
        }
        return responseData;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    async getPlaceDetail(nearPoiId, { custom = false, companyId = '', country = 'USA' } = {}) {
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const params = {};
        params.country = country;
        if (custom) {
          params.custom = true;
          if (companyId) params.companyId = companyId;
        }
        const response = await this.$http({
          method: 'GET',
          ...headers,
          params,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-detail/${encodeURIComponent(nearPoiId)}`,
          timeout: 15000,
        });
        return response.data;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    // Fetch the geometry for a single place. Reuses the places-detail endpoint,
    // which returns a normalized `geometry` (Polygon/MultiPolygon) built from the
    // upstream building bounds. When the place has no polygon (e.g. a Point-type
    // custom place), fall back to a Point geometry built from the place's
    // `lat`/`lon` so point-only places still resolve to a real geometry. Returns
    // null only when neither a polygon nor a valid point coordinate is available.
    async getPlaceGeometry(nearPoiId, { custom = false, companyId = '', country = 'USA' } = {}) {
      if (!nearPoiId) return null;
      const resp = await this.getPlaceDetail(nearPoiId, { custom, companyId, country });
      const data = resp && resp.data ? resp.data : null;
      if (!data) return null;
      const geometry = data.geometry || null;
      if (geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
        return geometry;
      }
      // Point fallback: build a GeoJSON Point from lat/lon when present. The
      // detail endpoint exposes numeric `lat`/`lon` attributes for every place,
      // including Point-type custom places that carry no building bounds.
      const lon = Number(data.lon);
      const lat = Number(data.lat);
      if (Number.isFinite(lon) && Number.isFinite(lat)) {
        return { type: 'Point', coordinates: [lon, lat] };
      }
      return null;
    },
    /**
     * Resolve polygon/point geometry for many places in a single batched path,
     * replacing the N+1 per-place `getPlaceGeometry` loop used by the selection
     * handoff. Deduplicates `near_poi_id`s (first-occurrence order), splits them
     * into ordered chunks of CHUNK_SIZE, and issues one POST per chunk to the
     * pinnacle-api Bulk_Route `POST /v2/places-bulk-detail`.
     *
     * Each returned entry is merged using the SAME geometry precedence as
     * `getPlaceGeometry`:
     *   1. `entry.geometry` when its type is Polygon/MultiPolygon;
     *   2. else a Point_Fallback `{type:'Point', coordinates:[lon, lat]}` built
     *      from finite `Number(entry.lon)` / `Number(entry.lat)`;
     *   3. else `null`.
     *
     * Failure isolation: each chunk is wrapped in try/catch. A rejection/timeout
     * maps every id in that chunk to `null` and never throws. After all chunks,
     * any deduped id still missing is backfilled to `null`, so `results.size`
     * equals the distinct requested id count. The method ALWAYS resolves to
     * `{ ok: true, results }` (never rejects, no `{ ok:false }` path).
     *
     * @param {Array<{near_poi_id:string, lat?:number, lon?:number}>} places
     * @param {{ custom?:boolean, companyId?:string, country?:string, timeout?:number }} [opts]
     *   `timeout` is the per-chunk axios timeout in ms (default 30000). Callers
     *   on an interactive path (e.g. a single-place zoom) may pass a shorter
     *   value so the request doesn't hang the interaction.
     * @returns {Promise<{ ok: true, results: Map<string, object|null> }>}
     */
    async resolveBulkGeometry(places, { custom = false, companyId = '', country = 'USA', timeout = 30000 } = {}) {
      const CHUNK_SIZE = 100;
      const results = new Map();

      // Empty short-circuit: null/undefined/length-0 -> no HTTP call.
      if (!Array.isArray(places) || places.length === 0) {
        return { ok: true, results };
      }

      // Dedupe near_poi_ids preserving first-occurrence order.
      const ids = [];
      const seen = new Set();
      places.forEach((place) => {
        const id = place && place.near_poi_id;
        if (id === undefined || id === null || id === '') return;
        if (seen.has(id)) return;
        seen.add(id);
        ids.push(id);
      });

      // Empty-after-dedupe short-circuit: no HTTP call.
      if (ids.length === 0) {
        return { ok: true, results };
      }

      // Split into ordered chunks of at most CHUNK_SIZE.
      const chunks = [];
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + CHUNK_SIZE));
      }

      // Sequential await loop: one request per chunk, failure isolated per chunk.
      for (const near_poi_ids of chunks) {
        try {
          if (this.tokenNeedsRefresh()) await this.refreshToken();
          const headers = this.getHeaders();
          const response = await this.$http({
            method: 'POST',
            data: { near_poi_ids, custom, companyId, country },
            ...headers,
            url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-bulk-detail`,
            timeout,
          });
          const data = response && response.data && Array.isArray(response.data.data)
            ? response.data.data
            : [];
          data.forEach((entry) => {
            if (!entry || entry.near_poi_id === undefined || entry.near_poi_id === null) return;
            // Geometry precedence mirrors getPlaceGeometry.
            const geometry = entry.geometry || null;
            if (geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
              results.set(entry.near_poi_id, geometry);
              return;
            }
            const lon = Number(entry.lon);
            const lat = Number(entry.lat);
            if (Number.isFinite(lon) && Number.isFinite(lat)) {
              results.set(entry.near_poi_id, { type: 'Point', coordinates: [lon, lat] });
              return;
            }
            results.set(entry.near_poi_id, null);
          });
        } catch (err) {
          // Failure isolation: map this chunk's ids to null, never throw.
          console.error('resolveBulkGeometry chunk failed', err);
          near_poi_ids.forEach((id) => results.set(id, null));
        }
      }

      // Backfill any deduped id not present so results.size === distinct id count.
      ids.forEach((id) => {
        if (!results.has(id)) results.set(id, null);
      });

      return { ok: true, results };
    },
    /**
     * Save a Custom place edit via POST {VUE_APP_PINNACLE_API}/v2/update-custom-place.
     *
     * Branches on geometry type (Requirement 5). For a Point place (`isPoint`
     * true) the body carries the point-specific fields — `lat`, `lon` — and NO
     * `geometry`, so the backend routes the update to the POI update endpoint.
     * For a Polygon place the body keeps sending `geometry` exactly as before,
     * so the polygon draw path is unchanged. The `isPoint` flag is a readability
     * hint only; the backend re-derives the geometry type from the fetched
     * record and is authoritative.
     *
     * Preserves the repo auth pattern (tokenNeedsRefresh -> refreshToken ->
     * getHeaders), the string-concatenated base URL, and the 30s timeout.
     *
     * @param {{near_poi_id:*, companyId:*, name:*, address:*, description:*, tags:*, category:*, brand:*, geometry:*, lat:*, lon:*, isPoint:boolean}} args
     * @returns {Promise<Object>}
     */
    async updateCustomPlace({ near_poi_id, companyId, name, address, description, tags, category, brand, geometry, lat, lon, isPoint = false }) {
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const body = { near_poi_id, companyId, name, address, description, tags };
        // Category -> cat2, Brand -> poi_list_name (Places API v3 field names).
        // Include only when present so a blank never clears the upstream value.
        if (category !== undefined && category !== null && String(category).trim() !== '') {
          body.cat2 = category;
        }
        if (brand !== undefined && brand !== null && String(brand).trim() !== '') {
          body.poi_list_name = brand;
        }
        if (geometry) {
          // A drawn polygon is present. Always send it: for a polygon edit this
          // is the edited geometry; for a Point edit it signals a Point ->
          // Polygon conversion (the backend routes a Point record with a valid
          // supplied geometry through the polygon draw path). Sending geometry
          // takes precedence over the point-only fields.
          body.geometry = geometry;
        } else if (isPoint) {
          // Point path with no drawing: carry point-specific fields and send NO
          // geometry so the backend forwards to the POI update endpoint.
          body.isPoint = true;
          body.lat = lat;
          body.lon = lon;
        }
        const response = await this.$http({
          method: 'POST',
          data: body,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/update-custom-place`,
          timeout: 30000,
        });
        return response.data;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    /**
     * Clone the current editor fields into a NEW custom place via
     * POST {VUE_APP_PINNACLE_API}/v2/save-custom-place.
     *
     * Builds the request body with the pure `buildClonePayload(...)` helper
     * first. When the builder aborts (geometry or companyId missing, R6.6/R6.8)
     * this resolves to `{ success: false, error: built.error }` WITHOUT making
     * any HTTP call, so the editor can surface a validation message.
     *
     * Otherwise follows the repo auth pattern (tokenNeedsRefresh -> refreshToken
     * -> getHeaders), POSTs `built.payload` with a 30s timeout, and returns
     * `response.data`. Axios errors (network/non-2xx/timeout) are rethrown so
     * the editor can map ECONNABORTED/timeout vs generic failure (R9.2). This is
     * intentionally distinct from `updateCustomPlace`: the create path never
     * sends `near_poi_id` (R5.6).
     *
     * @param {{companyId:*, name:*, address:*, description:*, tags:*, lat:*, lon:*, geometry:*}} args
     * @returns {Promise<Object|{success:false, error:{code:string, message:string}}>}
     */
    async saveCustomPlace({ companyId, name, address, description, tags, category, brand, lat, lon, geometry }) {
      const built = buildClonePayload({ companyId, name, address, description, tags, category, brand, lat, lon, geometry });
      if (built.ok === false) {
        // Precondition failed (R6.6 / R6.8) — no HTTP call.
        return { success: false, error: built.error };
      }
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'POST',
          data: built.payload,
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/save-custom-place`,
          timeout: 30000,
        });
        return response.data;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    /**
     * Apply one or more labels (tags) to a set of library places via
     * POST {VUE_APP_PINNACLE_API}/v2/add-labels.
     *
     * Empty-guard first (no HTTP): when `poiIds` or `labels` is not a non-empty
     * array, resolves to `{ success:false, error:{ code:'INVALID_ARGS', ... } }`
     * WITHOUT issuing a request (R7.1).
     *
     * Otherwise follows the repo auth pattern (tokenNeedsRefresh -> refreshToken
     * -> getHeaders, R7.2), POSTs `{ poiIds, labels, country, companyId }` with a
     * 30s timeout (R7.3), and defaults `country` to `"USA"` when the argument is
     * omitted (R7.4). On success returns `response.data` (R7.5). Unlike
     * `saveCustomPlace` (which rethrows), any failure/timeout/non-success resolves
     * to `{ success:false, error: axiosError }` so callers always get a failure
     * envelope to surface (R7.6).
     *
     * @param {{ companyId:string, poiIds:string[], labels:string[], country?:string }} args
     * @returns {Promise<Object|{success:false, error:*}>}
     */
    async addLabelsToPlaces({ companyId, poiIds, labels, country = 'USA' }) {
      if (!Array.isArray(poiIds) || poiIds.length === 0 || !Array.isArray(labels) || labels.length === 0) {
        // R7.1: nothing to send — no HTTP call.
        return { success: false, error: { code: 'INVALID_ARGS', message: 'poiIds and labels are required.' } };
      }
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'POST',
          data: { poiIds, labels, country, companyId },
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/add-labels`,
          timeout: 30000,
        });
        return response.data;
      } catch (axiosError) {
        // R7.6: failure/timeout/non-success -> failure envelope (do not throw).
        return { success: false, error: axiosError };
      }
    },
    /**
     * Fetch custom-place labels (tags) for the Tag filter typeahead from the
     * pinnacle-api labels-search proxy (POST /v2/places-labels-search).
     *
     * Returns [] immediately when no country is selected (skips the HTTP call),
     * matching the Category/Brand country-absent behavior (R5.5, R7).
     * Follows the repo auth pattern; NO tenantCode is ever sent (R5.7).
     * Normalizes the flat string array (or {text} objects) into [{ id, name }].
     *
     * @param {{ country: string, wildSearch?: string, companyId?: string, limit?: number }} args
     * @returns {Promise<Array<{id:string,name:string}>>}
     */
    async searchLabels({ country, wildSearch = '', companyId, limit = 50 } = {}) {
      if (!country) return []; // country-absent -> empty, skip request (R5.5, R7.1, R7.2)

      const baseUrl = process.env.VUE_APP_PINNACLE_API;
      if (!baseUrl || typeof baseUrl !== 'string' || baseUrl.trim() === '') {
        // Base URL not configured -> abort without an HTTP call (R5.3)
        throw new Error('VUE_APP_PINNACLE_API is not configured.');
      }

      if (this.tokenNeedsRefresh()) await this.refreshToken();
      const headers = this.getHeaders();

      const data = {
        country,
        wildSearch: (wildSearch || '').trim().slice(0, 256), // trimmed, max 256 (R5.6)
        companyId, // NO tenantCode (R5.7)
        limit,
      };

      const response = await this.$http({
        method: 'POST',
        data,
        ...headers,
        url: `${baseUrl}/v2/places-labels-search`,
      });

      return this._normalizeLabelOptions(response.data);
    },
    /**
     * Normalize a labels response into typeahead options [{ id, name }].
     * Accepts a flat array of label strings or objects with a `text` field.
     * Preserves order; skips empty/invalid entries; returns [] for non-arrays (R6.1-R6.5).
     * @param {*} payload
     * @returns {Array<{id:string,name:string}>}
     */
    _normalizeLabelOptions(payload) {
      if (!Array.isArray(payload)) return []; // R6.4
      const options = [];
      for (const entry of payload) {
        let value = null;
        if (typeof entry === 'string' && entry !== '') {
          value = entry; // R6.1 (no trim/truncate/case change)
        } else if (entry && typeof entry === 'object' && typeof entry.text === 'string' && entry.text !== '') {
          value = entry.text; // R6.2
        }
        if (value === null) continue; // skip invalid entry, keep converting the rest (R6.5)
        options.push({ id: value, name: value }); // R6.1, R6.3 (order preserved)
      }
      return options;
    },
    async searchCriteria(filter, query) {

      let params = {};
      if (this._paramList!=undefined){
        params = {...this._paramList};
      }
      console.log(filter, query);

      // if (filter.param === 'polygon_category.id') {
      //   path = '/polygon-categories';
      // } else {
      //   path = '/criteria';
      //   params['where[0]'] = `type.key=${filter.criterionKey}`;
      // }
      if (query.name !== undefined) params[`search_term`] = query.name;
      //if (query.key !== undefined) params[`where[${Object.keys(params).length}]`] = `key${query.operator !== undefined ? query.operator : '='}${query.key}`;
      if (query.page_size) params.page_size = query.page_size;
      if (query.country) params.country = query.country;
      if (query.state) params.state = query.state;
      //params[`field`] = filter.param;
      let location_type = filter.param;
      let path = suggest_urls[location_type]
      console.log(path)
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          ...headers,
          params,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/${path}`
        });

        let result = {}
        if (Object.keys(response.data).length > 0){
          if (['cat1','cat2'].includes(location_type)) {
            result = [];
            if (Object.keys(response.data).length > 0 && Object.keys(response.data.categories).length > 0){
              const uniques = [...new Set(response.data.categories.map(item => item.text))];
              result =uniques.map((item) => {
                return {
                  id: item,
                  name: item
                }
              });
            }
          }
          else if (location_type == "brand") {
            result = [];
            if (Object.keys(response.data).length > 0 && Object.keys(response.data.brands).length > 0){
              result =response.data.brands.map((item) => {
                return {
                  id: item.text,
                  name: item.text
                }
              });
            }
          }
          else if (location_type == "country") {
            result = Object.keys(response.data).filter(key => response.data[key].toLowerCase().includes(params[`search_term`].toLowerCase())).map((key) => {
              return {
                id: key,
                name: response.data[key]
              }
            }).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 5);
          } else if (location_type == "state") {
            result = [];
            if (Object.keys(response.data).length > 0 && Object.keys(response.data.STATE).length > 0){
              result =response.data.STATE.map((item) => {
                return {
                  id: item.text,
                  name: item.text
                }
              });
            }
          } else if (location_type == "city") {
            result = [];
            if (Object.keys(response.data).length > 0 && Object.keys(response.data.CITY).length > 0){
              result =response.data.CITY.map((item) => {
                return {
                  id: item.text,
                  name: item.text
                }
              });
            }
          }
        }

        return result;
      } catch (axiosError) {
        throw axiosError;
      }
    },
    /**
     * Resolve an Alpha3_Code to its canonical Country_Reference option
     * { id, name }, or null when the code has no matching option. The reference
     * is the same source that backs the Country filter options (the
     * `location_type=country` map of `{ alpha3 -> name }`), so the resolved name
     * matches a manual selection's chip label (R4.1, R4.2).
     *
     * Input is normalized (trim + uppercase); empty input returns null without
     * an HTTP call. Follows the repo auth pattern (tokenNeedsRefresh ->
     * refreshToken -> getHeaders). Any failure (request rejection/timeout,
     * unexpected/absent response shape) logs via console.error and resolves to
     * null so the caller degrades gracefully with no user-facing error
     * (R4.3, R5.2, R5.3). Never throws to the caller.
     *
     * @param {string} alpha3 an ISO 3166-1 alpha-3 country code (e.g. "USA")
     * @returns {Promise<{id:string,name:string}|null>}
     */
    async getCountryByAlpha3(alpha3) {
      const code = (alpha3 || '').trim().toUpperCase();
      if (!code) return null;
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken();
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          ...headers,
          params: { location_type: 'country' },
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-suggest`
        });
        // The country reference payload is a bare `{ alpha3: name }` map, same
        // shape read by the country branch of `searchCriteria` above.
        const map = response && response.data ? response.data : null;
        if (!map || typeof map !== 'object') return null;
        const name = map[code];
        if (typeof name !== 'string' || name === '') return null; // R4.3
        return { id: code, name }; // R4.1
      } catch (axiosError) {
        console.error('Failed to resolve country by alpha-3', code, axiosError);
        return null; // R5.2, R5.3
      }
    }
  }
}
